import type { Edge } from "@msuite/ui/dnd";
import { icon } from "@msuite/ui/icons";
import { Menu } from "@msuite/ui/menu";
import { MenuBarPrimitive } from "@msuite/ui/menu-bar";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { guardNumber } from "@msuite/utils/numbers";
import { ticketSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import type { Tables } from "@msuite/utils/types";
import { mutators } from "@msuite/zero";
import { useZero } from "@msuite/zero/react";
import { syncHandler } from "@/api/sync-handler";
import { TicketTypeIcon } from "@/components/cards";
import type { UseDaily } from "@/components/plan/daily/use-daily";
import { client } from "@/hono";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { TicketActionBarButton } from "./ticket-action-bar-button";
import { useCrewHandler } from "./use-crew-handler";

/** Props Interface */
interface TicketActionBarProps {
	ticket: Tables<"tickets">;
	daily: UseDaily;
}

export function TicketActionBar({ ticket, daily }: TicketActionBarProps) {
	/** Hooks */
	const qc = useQueryClient();
	const z = useZero();
	const crewHandler = useCrewHandler({ crewId: "", planId: guardString(daily.plan?.id) });
	const { updateIsDenied } = useTicketRules();

	/** Functions */
	async function handleOnChangeTicketState() {
		try {
			const response = await client.tickets[":ticketId"].$patch({
				json: {
					...ticketSchema.parse(ticket),
					uploadResults: [],
					state: "inactive",
				},
				param: {
					ticketId: ticket.id,
				},
			});
			await responseHandler(response);
			await qc.invalidateQueries({
				predicate: invalidate([ticket.id, ticket.projectId]),
			});
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnSkipTicket() {
		if (!daily.plan?.id) return;
		if (!ticket?.id) return;
		syncHandler(
			z.mutate(
				mutators.plans.toggleSkippedTicket({ planId: daily.plan?.id, ticketId: ticket?.id }),
			),
		);
	}

	function getInsertItem(
		crew: UseDaily["ownCrews"][number],
		item: UseDaily["ownCrews"][number]["ticketAssignments"][number],
		ticket: Tables<"tickets">,
		index: number,
		edge: Edge,
	) {
		return {
			label: `Position ${index + 1}`,
			icon: icon("Plus"),
			onClick: async () => {
				if (!ticket) return;
				crewHandler.setCrewId(crew.id);
				await crewHandler.onDropTicket(ticket, { edge, assignmentId: item.id });
				crewHandler.resetCrewId();
			},
		};
	}

	function buildQuickAssignOrganizationItem(crew: UseDaily["ownCrews"][number]) {
		const assignments = guardArray(crew.ticketAssignments);
		const items = assignments
			.map((ta) => {
				return {
					label: guardString(ta.ticket?.description),
					icon: null,
					item: ta,
					render: () => (
						<div
							className="h-stack grid gap-x-4"
							style={{ gridTemplateColumns: "max-content 1fr" }}
						>
							<TicketTypeIcon ticketTypeId={ta.ticket!.type!} />
							<div className="v-stack gap-0">
								<p className="label">{guardString(ta.project?.name)}</p>
								<p className="text-sm font-medium">{guardString(ta.ticket?.description)}</p>
							</div>
						</div>
					),
				};
			})
			.flatMap((item, index) => {
				if (index === 0) {
					return [
						getInsertItem(crew, item.item, ticket, index, "top"),
						item,
						getInsertItem(crew, item.item, ticket, index + 1, "bottom"),
					];
				} else {
					return [item, getInsertItem(crew, item.item, ticket, index + 1, "bottom")];
				}
			});

		return {
			label: `${guardString(crew.organization?.name)} ${guardNumber(crew.ranking)}`,
			icon: icon("User"),
			onClick: async () => {
				crewHandler.setCrewId(crew.id);
				const lastAssignment = crew.ticketAssignments?.at(0);
				if (lastAssignment) {
					await crewHandler.onDropTicket(ticket, {
						assignmentId: lastAssignment.id,
						edge: "bottom",
					});
				} else {
					await crewHandler.onDropTicket(ticket);
				}
				crewHandler.resetCrewId();
			},
			items: items.length > 0 ? items : undefined,
		};
	}

	/** Computed */
	// Three rules, not one. The red button retires the ticket through
	// `client.tickets[":ticketId"].$patch` — the ticket record, so the project relationship
	// alone decides. The green menu drops the ticket onto a column, which is arranging: `update
	// Plan` or the relationship, plus the published lock, because it writes the day. The amber
	// button skips the ticket on this plan, a column of the day, and stays the board's own
	// `update Plan`. Behind one flag the whole bar was hidden from everyone the board was closed
	// to, which is a rule the tickets domain never made.
	const canRetireTicket = !updateIsDenied(ticket);
	const canArrangeDay = !daily.isInteractionBlocked;
	const canAssignTicket = daily.canArrangeTicketOf(ticket.projectId);

	/** Guard */
	if (!daily.plan) return null;
	if (!canRetireTicket && !canArrangeDay && !canAssignTicket) return null;

	/** Render */
	return (
		<MenuBarPrimitive.Root className="h-stack items-center gap-1.5">
			{/* Retiring the ticket is a write on the ticket itself; skipping it for the day and
			    assigning it to a crew are writes on the board. */}
			{canRetireTicket && (
				<MenuBarPrimitive.Menu>
					<MenuBarPrimitive.Trigger onClick={handleOnChangeTicketState}>
						<TicketActionBarButton color="red" />
					</MenuBarPrimitive.Trigger>
				</MenuBarPrimitive.Menu>
			)}
			{canArrangeDay && (
				<MenuBarPrimitive.Menu>
					<MenuBarPrimitive.Trigger onClick={handleOnSkipTicket}>
						<TicketActionBarButton color="amber" />
					</MenuBarPrimitive.Trigger>
				</MenuBarPrimitive.Menu>
			)}
			{canAssignTicket && (
				<Menu
					type="menu-bar"
					align="center"
					items={() =>
						(daily.ownCrews || []).concat(daily.partnerCrews).map(buildQuickAssignOrganizationItem)
					}
					triggerAsChild={false}
				>
					<TicketActionBarButton color="emerald" />
				</Menu>
			)}
		</MenuBarPrimitive.Root>
	);
}
